export type RunLine = { kind: "log" | "warn" | "error" | "info"; text: string };
export type RunResult = { lines: RunLine[]; timedOut: boolean };
export type RunKind = "js" | "html";

// Shared with both sandboxes below: overrides console.* and window.onerror to
// report back to the parent via postMessage instead of the browser console.
const CONSOLE_CAPTURE_SNIPPET = `
  function __pcSend(kind, text) {
    parent.postMessage({ __pocketcode_run: true, kind: kind, text: text }, "*");
  }
  function __pcStringify(value) {
    if (value instanceof Error) return value.stack || value.message;
    if (typeof value === "object" && value !== null) {
      try { return JSON.stringify(value); } catch (e) { return String(value); }
    }
    return String(value);
  }
  ["log", "warn", "error", "info"].forEach(function (method) {
    console[method] = function () {
      __pcSend(method, Array.prototype.map.call(arguments, __pcStringify).join(" "));
    };
  });
  window.onerror = function (msg, src, line, col, err) {
    __pcSend("error", (err && err.stack) ? err.stack : (msg + " (line " + line + ")"));
    return true;
  };
`;

// Sandboxed (allow-scripts only, no allow-same-origin -> opaque, isolated
// origin) shell for plain-JS "Run". The user's code is sent afterwards via
// postMessage and run with eval *inside* that iframe, never concatenated
// into this HTML, so there's no </script> injection risk and no access to
// this page's DOM, cookies, or API.
const JS_SANDBOX_HTML = `<!DOCTYPE html><html><head></head><body><script>
(function () {
${CONSOLE_CAPTURE_SNIPPET}
  window.addEventListener("message", function (event) {
    var data = event.data;
    if (!data || !data.__pocketcode_exec) return;
    try {
      (0, eval)(data.code);
    } catch (err) {
      __pcSend("error", err && err.stack ? err.stack : String(err));
    }
    __pcSend("done", "");
  });
  parent.postMessage({ __pocketcode_run: true, kind: "ready" }, "*");
})();
<\/script></body></html>`;

export function runJavaScript(code: string, timeoutMs = 3000): Promise<RunResult> {
  return new Promise((resolve) => {
    const lines: RunLine[] = [];
    let settled = false;

    const iframe = document.createElement("iframe");
    iframe.sandbox.add("allow-scripts");
    iframe.style.display = "none";
    iframe.srcdoc = JS_SANDBOX_HTML;

    const finish = (timedOut: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      iframe.remove();
      resolve({ lines, timedOut });
    };

    const timer = setTimeout(() => finish(true), timeoutMs);

    function onMessage(event: MessageEvent) {
      if (event.source !== iframe.contentWindow) return;
      const data = event.data;
      if (!data || !data.__pocketcode_run) return;

      if (data.kind === "ready") {
        iframe.contentWindow?.postMessage({ __pocketcode_exec: true, code }, "*");
      } else if (data.kind === "done") {
        finish(false);
      } else {
        lines.push({ kind: data.kind, text: data.text });
      }
    }

    window.addEventListener("message", onMessage);
    document.body.appendChild(iframe);
  });
}

// Inserts the console-capture snippet into the user's own HTML so scripts
// embedded in it still report to the review panel, without altering how the
// page itself renders. Falls back to prepending it if there's no <head> or
// <html> tag to anchor on (e.g. a bare HTML fragment).
function injectConsoleCapture(html: string): string {
  const script = `<script>(function(){${CONSOLE_CAPTURE_SNIPPET}})();<\/script>`;

  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => `${match}\n${script}`);
  }
  if (/<html[^>]*>/i.test(html)) {
    return html.replace(/<html[^>]*>/i, (match) => `${match}\n${script}`);
  }
  return `${script}\n${html}`;
}

/**
 * Mounts a live, sandboxed preview of the given HTML into `container`.
 * Console messages from scripts inside the page stream to `onConsole` for
 * as long as the preview stays mounted. Returns a cleanup function that
 * tears the iframe down (call it when the review panel closes).
 */
export function mountHtmlPreview(
  container: HTMLElement,
  html: string,
  onConsole: (line: RunLine) => void
): () => void {
  const iframe = document.createElement("iframe");
  iframe.sandbox.add("allow-scripts");
  iframe.className = "h-full w-full border-0 bg-white";
  iframe.srcdoc = injectConsoleCapture(html);

  function onMessage(event: MessageEvent) {
    if (event.source !== iframe.contentWindow) return;
    const data = event.data;
    if (!data || !data.__pocketcode_run || data.kind === "ready" || data.kind === "done") return;
    onConsole({ kind: data.kind, text: data.text });
  }

  window.addEventListener("message", onMessage);
  container.appendChild(iframe);

  return () => {
    window.removeEventListener("message", onMessage);
    iframe.remove();
  };
}

export function getRunKind(path: string): RunKind | null {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "js" || ext === "mjs" || ext === "cjs") return "js";
  if (ext === "html" || ext === "htm") return "html";
  return null;
}
